import { flow, invert, pickBy, toString, uniq } from 'lodash';
import type { Selector } from 'reselect';

import type { RequestOptions } from '@swf/core/api/request';
import { AccountBrand, Brand, Device, FeatureFlag, OS, Site } from '@swf/core/enums';
import type {
  EffectParams,
  ModelConfig,
  RootState,
  SelectorCreator,
  Slicer
} from '@swf/core/store';
import { select } from '@swf/core/store';
import { canUseDOM, getLocaleCode } from '@swf/core/utils';

const BrandCodes = {
  [Brand.PokerStars]: 'PS',
  [Brand.StarsCasino]: 'SC',
  [Brand.BetStars]: 'BS',
  [Brand.FullTilt]: 'FT',
  [Brand.PokerStarsVegas]: 'VS',
  [Brand.SkyBet]: 'SKB',
  [Brand.FoxBet]: 'FXB'
};

export interface SiteState {
  accountBrand: AccountBrand;
  brand: Brand;
  region?: string;
  country: string;
  domain: string;
  embedded: boolean;
  id: Site;
  os: OS;
  legalAge?: number;
  locales: string[];
}

interface SiteModel extends ModelConfig<SiteState> {
  reducers: typeof reducers;
  selectors: typeof selectors;
  effects: typeof effects;
}

const effects = {
  async fetch(payload: undefined, { request, dispatch, rootState }: EffectParams) {
    const brand = select.site.getBaseBrand(rootState);
    const {
      body: { state, domain, site: id, device, ...site }
    } = await request({ service: 'session.info', meta: { brand } });
    dispatch.device.set({ device });
    dispatch.site.set(pickBy({ id, ...site }, Boolean));
  },
  async fetchLocales(payload: undefined, { dispatch, request, rootState }: EffectParams) {
    const locales = select.site.getLocales(rootState);
    const { body } = await request<string[]>({ service: 'cms.locales' });
    const CMSLocales = body.map(flow(getLocaleCode, toString)).filter(Boolean);
    dispatch.site.setLocales(
      select.site.isEmbedded(rootState) ? uniq([...CMSLocales, ...locales]) : CMSLocales
    );
  }
};

const reducers = {
  set: (state: SiteState, payload: Partial<SiteState>): SiteState => ({
    ...state,
    ...payload,
    brand: !payload.brand || payload.brand === Brand.Unknown ? state.brand : payload.brand
  }),
  setLocales: (state: SiteState, payload: string[]): SiteState => ({
    ...state,
    locales: payload
  })
};

const selectors = (slice: Slicer<SiteState>, createSelector: SelectorCreator) => ({
  getId() {
    return slice((state) => state.id);
  },
  getAccountBrand() {
    return slice((state) => state.accountBrand);
  },
  getLegalAge() {
    return slice((state) => state.legalAge);
  },
  getLocales() {
    return slice((state) => state.locales);
  },
  getDomain() {
    return slice((state) => state.domain);
  },
  getName() {
    return slice((state) => invert(Site)[state.id]);
  },
  getBaseBrand() {
    return slice((state) => state.brand);
  },
  getBrand() {
    return createSelector(
      slice,
      (rootState) => select.page.getMeta(rootState),
      (site, meta) => meta.brand || site.brand
    );
  },
  getBrandName(this: { getBrand: Selector<RootState, Brand> }) {
    return createSelector(this.getBrand, (brand) => invert(Brand)[brand]);
  },
  getBrandCode(this: { getBrand: Selector<RootState, Brand> }) {
    return createSelector(this.getBrand, (brand) => BrandCodes[brand]);
  },
  getCountry() {
    return slice((state) => state.country);
  },
  isEmbedded() {
    return slice((state) => !!state.embedded);
  },
  isDesktopEmbedded() {
    return createSelector(
      slice,
      (rootState) => [Device.Desktop, Device.Other].includes(rootState.device),
      (state, desktopOrOther) => state.embedded && desktopOrOther
    );
  },
  isMobileEmbedded() {
    return createSelector(
      slice,
      (rootState) => [Device.Phone, Device.Tablet].includes(rootState.device),
      (state, mobile) => state.embedded && mobile
    );
  },
  isAndroid() {
    return slice((state) => state.os === OS.Android);
  },
  getOS() {
    return slice((state) => state.os);
  },
  showVegasContent(this: {
    getBrand: Selector<RootState, Brand>;
    getId: Selector<RootState, Site>;
  }) {
    return createSelector(
      this.getBrand,
      this.getId,
      (brand, site) => brand === Brand.PokerStarsVegas || site === Site.DESH
    );
  },
  hasMultiBrandCountry() {
    return createSelector(
      slice,
      (rootState) => select.feature.check(rootState, FeatureFlag.SkyBetSports),
      (rootState) => select.session.isAuthenticated(rootState),
      ({ id }, skyBetSports, authenticated) =>
        skyBetSports && id !== Site.IT && (!authenticated || [Site.EU, Site.COM].includes(id))
    );
  },
  getRegion() {
    return slice((state) => state.region);
  },
  getRequestOptions() {
    return createSelector(
      slice,
      (rootState) => select.site.getBrand(rootState),
      (rootState) => select.currency.get(rootState),
      (rootState) => select.locale.get(rootState),
      (rootState) => select.session.get(rootState),
      (rootState) => select.device.get(rootState),
      (rootState) => select.feature.check(rootState, FeatureFlag.JWTEnabled),
      (rootState) => select.location.getHost(rootState),
      (rootState) => select.location.get(rootState),
      (
        { id, region, embedded, country, os },
        brand,
        currency,
        locale,
        session,
        device,
        JWTEnabled,
        host,
        { href }
      ) => {
        const meta: RequestOptions['meta'] = { locale, currency, site: id, brand };
        const headers: Record<string, string> = { referer: href };

        if (!canUseDOM) {
          Object.assign(meta, { device, country, os, embedded: embedded ? 1 : 0 });
          headers['x-forwarded-host'] = host;
          if (region) meta.region = region;
        }

        if (!JWTEnabled && session.token && session.signature) {
          meta.token = session.token;
          meta.signature = session.signature;
        } else if (JWTEnabled && session.token) {
          headers.Authorization = `StarsWeb-Session ${session.token}`;
        }

        return { meta, headers };
      }
    );
  },
  getNotificationSubscriptionPath(this: { getId: Selector<RootState, Site> }) {
    return createSelector(
      (rootState) => select.locale.get(rootState),
      (rootState) => select.device.get(rootState),
      (rootState) => select.account.getLicense(rootState),
      (rootState) => select.account.getUserId(rootState),
      this.getId,
      (locale, device, license, userId, site) =>
        `/user/queue/starsweb/notifications/${locale}/${device}/${license}/${site}/${userId}`
    );
  },
  getStarsDomain() {
    return slice(
      ({ id }): string =>
        ({
          [Site.IT]: 'starsaccount.it',
          [Site.GR]: 'starsaccount.gr',
          [Site.FR]: 'starsaccount.fr',
          [Site.ES]: 'starsaccount.es',
          [Site.DK]: 'starsaccount.dk',
          [Site.EU]: 'starsaccount.eu',
          [Site.UK]: 'starsaccount.uk',
          [Site.BG]: 'starsaccount.bg',
          [Site.DESH]: 'sh.starsaccount.de',
          [Site.RO]: 'starsaccount.ro',
          [Site.EE]: 'starsaccount.ee',
          [Site.CZ]: 'starsaccount.cz',
          [Site.BE]: 'starsaccount.be',
          [Site.RUSOPM]: 'starsaccount.net',
          [Site.PT]: 'starsaccount.pt',
          [Site.SE]: 'starsaccount.se',
          [Site.NET]: 'starsaccount.net',
          [Site.USPA]: 'starsaccountpa.com',
          [Site.USNJ]: 'pokerstarscasinonj.com',
          [Site.USMI]: 'pokerstarscasinomi.com',
          [Site.NL]: 'pokerstars.nl'
        }[id as number] || 'starsaccount.com')
    );
  },
  isSiteUS() {
    return slice((state) => [Site.USMI, Site.USNJ, Site.USPA, Site.USCO].includes(state.id));
  },
  isMultiBrand(this: {
    hasMultiBrandCountry: Selector<RootState, boolean>;
    isSiteUS: Selector<RootState, boolean>;
    getId: Selector<RootState, Site>;
  }) {
    return createSelector(
      (rootState) => select.feature.check(rootState, FeatureFlag.SkyBetSports),
      this.hasMultiBrandCountry,
      this.isSiteUS,
      this.getId,
      (skyBetSports, hasMultiBrandCountry, isSiteUS, site) =>
        hasMultiBrandCountry || isSiteUS || (site === Site.IT && skyBetSports) || site === Site.DESH
    );
  },
  isVegasBrand(this: { getBrand: Selector<RootState, Brand> }) {
    return createSelector(this.getBrand, (brand) => brand === Brand.PokerStarsVegas);
  }
});

const site: SiteModel = {
  state: {
    accountBrand: AccountBrand.StarsAccount,
    brand: Brand.StarsCasino,
    country: 'CA',
    domain: '',
    id: Site.COM,
    embedded: false,
    os: OS.Unknown,
    locales: []
  },
  reducers,
  effects,
  selectors
};

export default site;
